import { useEffect, useRef } from 'react';
import type {
  FolderNameProblem,
  OfferteLookup,
  ProjectFolderScan,
  ProjectmapDetail,
  ProjectmapQueueRow,
} from '@/api/projectmap';

/**
 * One job's detail: offerte folder (Sales), existing project folder
 * (Projecten), and the folder the portal would make. Nothing here creates
 * anything.
 *
 * The wording carries weight: only `none` may say a folder is absent. A search
 * that failed or could not see every year says exactly that, because "geen map"
 * is what would later lead someone to make a duplicate.
 */

export type DetailState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; detail: ProjectmapDetail };

const PROBLEM_TEXT: Record<FolderNameProblem, string> = {
  geen_snummer: 'Geen verkoopordernummer — zonder ordernummer kan er geen naam gemaakt worden.',
  geen_jaar: 'De verkooporder is niet bevestigd — er is geen bevestigingsdatum voor het jaar.',
  geen_werf: 'De verkooporder heeft geen werfnaam.',
  geen_klant: 'De verkooporder heeft geen klant.',
};

/**
 * `cachedUrl` is an Odoo Studio text field any Odoo user can edit, so it is a
 * link only when it is an https SharePoint address — never `javascript:` or
 * anything else.
 */
export function isSharepointUrl(value: string | null): value is string {
  if (!value) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && (url.hostname === 'sharepoint.com' || url.hostname.endsWith('.sharepoint.com'));
  } catch {
    return false;
  }
}

const linkClass = 'text-td-blue underline underline-offset-2 hover:text-td-navy font-medium break-words';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-td-muted">{title}</h3>
      <div className="text-sm text-td-navy space-y-1.5">{children}</div>
    </section>
  );
}

function Badge({ tone, children }: { tone: 'ok' | 'neutral' | 'warn'; children: React.ReactNode }) {
  const cls =
    tone === 'ok' ? 'bg-emerald-100 text-emerald-800' : tone === 'warn' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700';
  return <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${cls}`}>{children}</span>;
}

function FolderLink({ name, webUrl, path }: { name: string; webUrl: string; path?: string }) {
  return (
    <a href={webUrl} target="_blank" rel="noopener noreferrer" title={path} className={linkClass}>
      {name}
    </a>
  );
}

function OfferteSection({ offerte }: { offerte: OfferteLookup }) {
  const partial = !offerte.complete && (offerte.outcome === 'found' || offerte.outcome === 'ambiguous');
  return (
    <Section title="Offertemap (Sales)">
      {offerte.outcome === 'found' && offerte.folder && (
        <>
          <Badge tone="ok">Offertemap gevonden</Badge>
          <p><FolderLink {...offerte.folder} /></p>
        </>
      )}
      {offerte.outcome === 'ambiguous' && (
        <>
          <Badge tone="warn">Meerdere mogelijke offertemappen</Badge>
          <ul className="list-disc pl-5 space-y-1">
            {offerte.candidates.map((c) => (
              <li key={c.webUrl}><FolderLink {...c} /></li>
            ))}
          </ul>
          {offerte.candidateCount > offerte.candidates.length && (
            <p className="text-td-muted">en nog {offerte.candidateCount - offerte.candidates.length} andere</p>
          )}
        </>
      )}
      {offerte.outcome === 'none' && <Badge tone="neutral">Geen offertemap gevonden</Badge>}
      {offerte.outcome === 'incomplete' && (
        <>
          <Badge tone="warn">Zoeken onvolledig</Badge>
          <p>Niet alle jaren in de Sales-map konden doorzocht worden. Dit betekent niet dat er geen offertemap is.</p>
        </>
      )}
      {offerte.outcome === 'unavailable' && (
        <>
          <Badge tone="warn">Sales-map niet bereikbaar</Badge>
          <p>Het portaal mag de Sales-map (THDTSales) niet lezen. Er is niet gezocht.</p>
        </>
      )}
      {partial && <p className="text-amber-800">Niet alle jaren konden doorzocht worden.</p>}
    </Section>
  );
}

function ProjectFolderSection({ scan }: { scan: ProjectFolderScan }) {
  return (
    <Section title="Projectmap (Projecten)">
      {scan.outcome === 'found' && scan.folder && (
        <>
          <Badge tone="warn">Er bestaat al een projectmap</Badge>
          <p><FolderLink {...scan.folder} /></p>
        </>
      )}
      {scan.outcome === 'cached' && (
        <>
          <Badge tone="warn">Projectmap bekend in Odoo</Badge>
          {isSharepointUrl(scan.cachedUrl) ? (
            <p><a href={scan.cachedUrl} target="_blank" rel="noopener noreferrer" className={linkClass}>Map openen</a></p>
          ) : (
            scan.cachedUrl && <p className="text-amber-800">De link in Odoo is geen SharePoint-adres en wordt niet geopend.</p>
          )}
        </>
      )}
      {scan.outcome === 'none' && <Badge tone="ok">Nog geen projectmap</Badge>}
      {scan.outcome === 'incomplete' && (
        <>
          <Badge tone="warn">Zoeken onvolledig</Badge>
          <p>Niet alles in de Projecten-map was zichtbaar. Dit is geen bewijs dat er geen projectmap is.</p>
        </>
      )}
      {scan.outcome === 'unresolved' && <Badge tone="warn">Project niet gevonden in Odoo</Badge>}
    </Section>
  );
}

function PreviewSection({ detail }: { detail: ProjectmapDetail }) {
  const { preview } = detail;
  return (
    <Section title="Voorstel nieuwe projectmap">
      {preview.path ? (
        <p className="font-mono text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5 break-words" data-testid="preview-path">
          {/* A line break may fall after each "/" and at spaces, never mid-word unless a word is wider than the box. */}
          {preview.path.split('/').map((segment, i, all) => (
            <span key={i}>
              {segment}
              {i < all.length - 1 && (
                <>
                  /<wbr />
                </>
              )}
            </span>
          ))}
        </p>
      ) : (
        <Badge tone="warn">Geen voorstel mogelijk</Badge>
      )}
      {preview.shortened.werf && <p className="text-amber-800">De werfnaam is ingekort zodat de map niet te lang wordt.</p>}
      {preview.shortened.klant && <p className="text-amber-800">De klantnaam is ingekort zodat de map niet te lang wordt.</p>}
      {preview.cleaned && <p className="text-amber-800">Tekens die SharePoint niet toelaat zijn weggelaten.</p>}
      {preview.problems.map((p) => (
        <p key={p} className="text-amber-800">{PROBLEM_TEXT[p]}</p>
      ))}
      <p className="text-xs text-td-muted">Alleen een voorstel: er wordt niets aangemaakt.</p>
    </Section>
  );
}

export function ProjectmapDetailPanel({
  row,
  state,
  onRetry,
  onClose,
}: {
  row: ProjectmapQueueRow;
  state: DetailState;
  onRetry: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLElement>(null);

  // On a phone the panel sits above a long list: a tap on a row further down
  // must bring it into view, or the tap looks like it did nothing.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { top, bottom } = el.getBoundingClientRect();
    if (top < 0 || bottom > window.innerHeight) el.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [row.taskId]);

  return (
    <aside
      ref={ref}
      aria-label="Details projectmap"
      className="bg-white rounded-xl border border-slate-200 p-4 space-y-4 scroll-mt-4 lg:sticky lg:top-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-extrabold text-td-navy break-words">{row.saleOrderProjectName ?? row.projectName}</h2>
          <p className="text-sm text-td-muted">
            {[row.saleOrderName, row.klant, row.projectleider].filter(Boolean).join(' · ')}
          </p>
          <a href={row.projectOdooUrl} target="_blank" rel="noopener noreferrer" className={`text-sm ${linkClass}`}>
            Openen in Odoo
          </a>
        </div>
        <button type="button" onClick={onClose} aria-label="Details sluiten" className="text-td-muted hover:text-td-navy font-bold text-lg leading-none">
          ×
        </button>
      </div>

      {state.status === 'loading' && (
        <p className="text-sm text-td-muted" role="status">SharePoint doorzoeken...</p>
      )}
      {state.status === 'error' && (
        <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm space-y-2" role="alert">
          <p>Zoeken mislukt: {state.message}</p>
          <button type="button" onClick={onRetry} className="font-semibold underline">Opnieuw proberen</button>
        </div>
      )}
      {state.status === 'ready' && (
        <>
          <OfferteSection offerte={state.detail.offerte} />
          <ProjectFolderSection scan={state.detail.projectFolder} />
          <PreviewSection detail={state.detail} />
          <button type="button" onClick={onRetry} className="text-sm font-semibold text-td-blue hover:underline">
            Opnieuw zoeken
          </button>
        </>
      )}
    </aside>
  );
}
